import React, { useLayoutEffect, useRef } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { FieldProductionItem } from '../../../types/WellSummaryDashboardTypes';

// Transform data to amCharts format
function transformData(data: FieldProductionItem[]) {
  // 1. Collect all unique x values
  const allX = new Set<number>();
  data.forEach((series) => series.coordinates.forEach((c) => allX.add(c.x)));
  const xArr = Array.from(allX).sort((a, b) => a - b);

  // 2. Build lookup for each MOP
  const lookup: Record<string, Record<number, number>> = {};
  data.forEach((series) => {
    lookup[series.mop] = {};
    series.coordinates.forEach((c) => {
      lookup[series.mop][c.x] = c.y;
    });
  });

  // 3. Build chart data array
  return xArr.map((x) => {
    const point: Record<string, number | null> = { day: x };
    Object.keys(lookup).forEach((mop) => {
      point[mop] = lookup[mop][x] ?? null;
    });
    return point;
  });
}
function updateYAxisMinMax(seriesList: am5xy.LineSeries[], yAxis: am5xy.ValueAxis<am5xy.AxisRenderer>) {
  let min = Infinity;
  let max = -Infinity;
  let hasVisible = false;

  seriesList.forEach((series) => {
    if (series.get('visible')) {
      hasVisible = true;
      series.dataItems.forEach((dataItem) => {
        const value = dataItem.get('valueY');
        if (typeof value === 'number' && !isNaN(value)) {
          if (value < min) min = value;
          if (value > max) max = value;
        }
      });
    }
  });

  if (hasVisible && min !== Infinity && max !== -Infinity) {
    // Snap to nearest 500 for min/max
    const snappedMin = Math.floor(min / 500) * 500;
    const snappedMax = Math.ceil(max / 500) * 500;
    yAxis.set('min', snappedMin);
    yAxis.set('max', snappedMax);
  } else {
    yAxis.set('min', 0);
    yAxis.set('max', undefined);
  }
}
interface FieldProductionChartProps {
  rawData: FieldProductionItem[];
  fullscreen: boolean;
}

const FieldProductionChart: React.FC<FieldProductionChartProps> = ({ rawData, fullscreen }) => {
  const chartRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const root = am5.Root.new(chartRef.current!);

    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        panY: true,
        wheelX: 'panX',
        wheelY: 'zoomX',
        layout: root.verticalLayout,
      }),
    );

    // X Axis (Days)
    const xAxis = chart.xAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererX.new(root, {}),
        tooltip: am5.Tooltip.new(root, {}),
        min: 0,
      }),
    );
    xAxis.get('renderer').labels.template.setAll({ text: '{value}', fill: am5.color(0xffffff) });
    xAxis.get('renderer').grid.template.setAll({ stroke: am5.color(0xffffff), strokeOpacity: 0.1 });
    xAxis.children.push(
      am5.Label.new(root, {
        text: 'Days',
        fill: am5.color(0xffffff),
        fontWeight: 'bold',
        centerX: am5.p50,
        x: am5.p50,
        paddingTop: 20,
      }),
    );

    xAxis.get('renderer').labels.template.setAll({ text: '{value}' });

    // Y Axis (Production)
    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {}),
        tooltip: am5.Tooltip.new(root, {}),
      }),
    );
    yAxis.get('renderer').labels.template.setAll({ fill: am5.color(0xffffff) });
    yAxis.get('renderer').grid.template.setAll({ stroke: am5.color(0xffffff), strokeOpacity: 0.1 });
    yAxis.children.unshift(
      am5.Label.new(root, {
        text: 'Oil production (bopd)',
        fill: am5.color(0xffffff),
        fontWeight: 'bold',
        rotation: -90,
        y: am5.percent(60),
        paddingRight: 20,
      }),
    );

    const chartData = transformData(rawData);

    // Find all MOPs with data
    const mops = rawData.filter((s) => s.coordinates.length > 0).map((s) => s.mop);

    // Store series references for legend
    const seriesList: am5xy.LineSeries[] = [];

    // Add a line series for each MOP
    mops.forEach((mop) => {
      const series = chart.series.push(
        am5xy.LineSeries.new(root, {
          name: mop,
          xAxis,
          yAxis,
          valueXField: 'day',
          valueYField: mop,
          tooltip: am5.Tooltip.new(root, {
            labelText: `${mop}: {${mop}}`,
          }),
        }),
      );
      series.strokes.template.setAll({
        strokeWidth: 2,
      });
      series.data.setAll(chartData);
      seriesList.push(series);
    });
    const legend = chart.children.push(
      am5.Legend.new(root, {
        useDefaultMarker: true,
      }),
    );
    legend.labels.template.setAll({
      fill: am5.color(0xffffff),
    });

    legend.data.setAll(seriesList);
    legend.itemContainers.each((item) => {
      item.events.on('click', () => {
        setTimeout(() => updateYAxisMinMax(seriesList, yAxis), 0);
      });
    });
    // Add cursor
    chart.set('cursor', am5xy.XYCursor.new(root, {}));

    // Cleanup
    return () => {
      root.dispose();
    };
  }, [rawData]);

  return (
    <div
      id='fop-chart-container'
      ref={chartRef}
      style={{ width: '100%', height: fullscreen ? '100%' : 'calc(100% - 40px)' }}
    />
  );
};

export default FieldProductionChart;
